//Imports
import React, {
    Fragment,
    useEffect,
    useRef,
    useState,
} from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { getTagColor, completeColumns } from 'Core/data/Helpers';
import { useNavigate } from 'react-router-dom';

//Actions
import { openModalCreateEditCustomer } from './modals/createEditCustomer/redux/slice'
import { reset, workOrdersSearchCustomers } from "./redux/slice";
import {
    openModalTransactionsSummary,
    openModalImportCustomers,
    openModalDeleteCustomer,
    sendGenericExport,
    savePreference,
    clearRedux,
} from "Redux/actions";

//Controls
import {
    Container,
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination,
    Link
} from "@gpstrackit/gtc-react-controls";
import { TagsGroup } from 'Components/Tag';

//Components
import { NotificationManager } from "../../../../components/Notifications";
import MapModal from "Modules/.common/components/MapModal";
import Preferences from "Core/containers/Preferences";
import { Grid, Icon } from "@mui/material";

//Modals
import CreateEditCustomer from './modals/createEditCustomer'
import ImportCustomers from './modals/importCustomers'
import TransactionsSummary from 'Modules/.common/components/TransactionsSummary';
import DeleteCustomer from 'Modules/workOrders/views/customerTab/modals/deleteCustomer';

//Variables
const KEY_PREFERENCE = "WOCustomerTab-list";

//Styles
import "./index.scss";

const CustomerTab = () => {


    const navigate = useNavigate();

    //States
    const [selectedItems, setSelectedItems] = useState([]);
    const [preferences, setPreferences] = useState({});
    const [dataToMap, setDataToMap] = useState({});

    //Table refs
    const customerTableRef = useRef(null);

    //Language
    const { messages, locale } = useIntl();

    //Redux States
    const { loadingCustomers, customers, totalCustomers, error } = useSelector((state) => state.workOrdersCustomerTabRedux);
    const migrationPushNotification = useSelector(state => state.catalogsRedux.migrationPushNotification);
    const openModalCustomer = useSelector((state) => state.workOrdersCreateEditCustomerRedux.open);
    const openModalTransactions = useSelector((state) => state.transactionsSummaryRedux?.open);
    const openModalImport = useSelector((state) => state.workOrdersImportCustomersRedux.open);
    const openModalDelete = useSelector((state) => state.workOrdersDeleteCustomerRedux.open);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);

    const dispatch = useDispatch();
    const paginationCustomers = useTablePagination({
        dispatch,
        action: workOrdersSearchCustomers,
        searchFields: ["id", "name", "ein", "customerContact_name", "customerContact_phone", "customerContact_email"],
        tableRef: customerTableRef,
        sendGenericExport,
        exportName: "customers",
    });

    //Options
    const getCustomerMenuOptions = () => [
        messages["edit"],
        messages["delete"]
    ];

    //Handlers and utils
    const handleClickCustomerMenu = (type, customer) => {
        if (type === messages["edit"]) {
            dispatch(openModalCreateEditCustomer({ customerId: customer?.id }))
        } else if (type === messages["delete"]) {
            dispatch(openModalDeleteCustomer({ customer: customer }))
        }
    };

    const refreshTable = () => {
        setSelectedItems([]);
        customerTableRef?.current?.setPage(1);
        customerTableRef?.current?.setSelectedItems([]);
        paginationCustomers.onChangePage(1);
    };

    const savePreferences = (key, value) => {
        let preferenceValue = { ...preferences, [key]: value }
        setPreferences(preferenceValue)
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: KEY_PREFERENCE, preferenceValue: preferenceValue }));
    }

    //Columns
    let currentColumns = [
        {
            key: "name",
            title: messages['workOrder_customerTab_name'],
            functionString: 'return row?.name',
            sort: true,
            sortField: "name",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.name,
            render: (row) => (
                <Grid container alignItems="center">
                    <Grid item xs={10} style={{ wordWrap: 'break-word', width: 200 }}>
                        <Link
                            className="table-span customer-tab-link-button"
                            dest={`/app/workorders/customerDetail/${row.id}`}
                            onClick={() => navigate(`/app/workorders/customerDetail/${row.id}`)}
                            text={row?.name}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <MenuComponent
                            placement="right-start"
                            options={getCustomerMenuOptions(row?.unit?.label)}
                            icon="more_vert"
                            className="showInHover"
                            onClick={handleClickCustomerMenu}
                            value={row}
                        />
                    </Grid>
                </Grid>
            )
        },
        {
            key: "ein",
            title: messages['workOrder_customerTab_eIn'],
            functionString: 'return row?.ein',
            sort: true,
            sortField: "ein",
            show: true,
            export: true,
            width: 150,
            exportValue: (row) => row?.ein,
            render: (row) => <span>{row?.ein}</span>
        },
        {
            key: "customerContact_name",
            title: messages['workOrder_customerTab_contact'],
            functionString: 'return row?.customerContact?.name',
            sort: true,
            sortField: "customerContact_name",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.customerContact?.name,
            render: (row) => <span>{row?.customerContact?.name}</span>
        },
        {
            key: "customerContact_email",
            title: messages['workOrder_customerTab_email'],
            functionString: 'return row?.customerContact?.email',
            sort: true,
            sortField: "customerContact_email",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.customerContact?.email,
            render: (row) => <span>{row?.customerContact?.email}</span>
        },
        {
            key: "customerContact_phone",
            title: messages['workOrder_customerTab_phone'],
            functionString: 'return row?.customerContact?.phone',
            sort: true,
            sortField: "customerContact_phone",
            show: true,
            export: true,
            width: 150,
            exportValue: (row) => row?.customerContact?.phone,
            render: (row) => <span>{row?.customerContact?.phone}</span>
        },
        {
            key: "tags",
            title: "Tags",
            functionString: `
                return row?.tags?.filter(tag => row?.tagIds?.includes(tag.id)).map(item => item?.label).join("/");
            `,
            show: true,
            export: true,
            width: 150,
            exportValue: (row) => {
                return row.tags.filter(tag => row.tagIds.includes(tag.id)).map(x => x.label).join('/\r\n');
            },
            render: (row) => {
                let unitTags = [];
                if (row?.tagIds?.includes("-99") || row?.tagIds?.includes(-99)) {
                    unitTags = [{ id: -99, label: messages['tagPicker_allTagsAreSelected'], color: '#005bf8' }];
                } else if (row?.tags && row?.tags?.length) {
                    unitTags = row?.tags?.filter((item) => row?.tagIds.includes(item?.id)).map(t => { return { ...t, color: getTagColor(t?.color) } });
                }

                return (
                    <TagsGroup
                        options={unitTags}
                        nameField="label"
                        colorField="color"
                    />
                )
            }
        },
        {
            key: "customerLocation",
            title: messages['workOrder_customerTab_address'],
            functionString: `
                let currentAddress = ""
                try {
                    currentAddress = JSON.parse(row?.customerLocation?.address)
                } catch (error) { }
                return currentAddress?.address
            `,
            show: true,
            export: true,
            width: 400,
            exportValue: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.customerLocation?.address)
                } catch (error) { }
                return currentAddress?.address
            },
            render: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.customerLocation?.address)
                } catch (error) { }

                return (
                    <Grid container alignItems="center">
                        {currentAddress?.address && (
                            <Grid
                                className="trackerTab-location-address"
                                item
                                onClick={() => {
                                    const data = {
                                        show: true,
                                        title: currentAddress.address,
                                        isPointerMarker: true,
                                        pointers: [
                                            {
                                                latitude: row.customerLocation?.latitude,
                                                longitude: row.customerLocation?.longitude,
                                                label: "Map Point",
                                                heading: 0,
                                                color: "000",
                                                eventName: "no event",
                                                draggable: false
                                            }
                                        ]
                                    };
                                    setDataToMap(data);
                                }}
                            >
                                <Icon className="iconFlag">room</Icon>
                                <p>{currentAddress.address || ""}</p>
                            </Grid>
                        )}
                    </Grid>
                )
            }
        }
    ]

    useEffect(() => {
        paginationCustomers.setColumns(currentColumns);
        setSelectedItems([]);
        customerTableRef?.current?.setPage(1);
        customerTableRef?.current?.setSelectedItems([]);
    }, []);

    useEffect(() => {
        paginationCustomers.refresh({ _sort: { key: "id", order: "asc" } });
        return () => dispatch(reset());
    }, [])

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages["errorMessage"],
                messages["error"],
                3000,
                null,
                null,
                "warning"
            );
            dispatch(reset({ error: null }));
        }
        if (exportResult && exportName === "customers") {
            NotificationManager.info(
                messages["exportInProcessBody"],
                messages["exportInProcessTitle"],
                4000,
                null,
                null,
                "info",
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [error, exportResult, exportName]);

    useEffect(() => {
        if (migrationPushNotification?.transactionId && migrationPushNotification?.migrationType === 'ImportCustomers') {
            NotificationManager.success(
                messages['workOrder_customerTab_importFinished'],
                messages['workOrder_customerTab_seeSummary'],
                4000,
                () => {
                    dispatch(openModalTransactionsSummary({ transactionId: migrationPushNotification?.transactionId }));
                    dispatch(openModalImportCustomers({ open: false }));
                    refreshTable();
                },
                null,
                'success',
                null
            );
            dispatch(clearRedux("CATALOG_REDUX", { migrationPushNotification: {} }))
        }
    }, [migrationPushNotification]);

    useEffect(() => {
        if (preferences?.columns?.length) {
            const preferenceMap = new Map(preferences.columns.map(col => [col.key, col]));
            // Reorder columns based on preferences (keeping `show`)
            const reorderedColumns = preferences.columns
                .map(pref => {
                    const col = currentColumns.find(c => c.key === pref.key);
                    return col ? { ...col, show: pref.show } : null;
                })
                .filter(Boolean);
            // Add new columns (not listed in preferences), with `show: true` by default
            const newColumns = currentColumns
                .filter(col => !preferenceMap.has(col.key))
                .map(col => ({ ...col, show: true }));

            const finalColumns = [...reorderedColumns, ...newColumns];
            paginationCustomers.setColumns(finalColumns);
            setPreferences({ ...preferences, columns: [] });
        }
    }, [preferences.columns])

    useEffect(() => {
        if (paginationCustomers.columns.length > 0) {
            setPreferences({ ...preferences, columns: paginationCustomers.columns })
        }
    }, [locale]);

    return (
        <Fragment>
            <Container maxWidth={false}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item xs={6} sm={2}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="center"
                            spacing={1}
                        >
                            <Grid item style={{ display: "flex" }}>
                                <IconButton
                                    tooltip={messages["add"]}
                                    placement="bottom"
                                    variant="secondary"
                                    icon="add_box"
                                    size="small"
                                    disabled={true || loadingCustomers}
                                    onClick={() => {
                                        dispatch(openModalCreateEditCustomer({}))
                                    }}
                                />
                                {
                                    selectedItems?.length ? (
                                        <IconButton
                                            tooltip={messages["delete"]}
                                            placement="bottom"
                                            variant="secondary"
                                            icon="delete"
                                            size="small"
                                            onClick={() => {
                                                dispatch(openModalDeleteCustomer({ customerList: selectedItems }))
                                            }}
                                            style={{ opacity: !selectedItems.length && "0.6" }}
                                            isDisabled={!selectedItems.length}
                                        />
                                    ) : <></>
                                }

                                <TableButtons
                                    messages={messages}
                                    {...paginationCustomers}
                                    buttons={["export", "orderColumn"]}
                                    onExport={(type) => {
                                        paginationCustomers.onExport(type);
                                    }}
                                    onChangeColumns={(columns) => {
                                        savePreferences("columns", columns)
                                        paginationCustomers.setColumns(columns)
                                    }}
                                />
                                <IconButton
                                    tooltip={messages['workOrder_customerTab_importCustomers']}
                                    placement="bottom"
                                    variant="secondary"
                                    icon="ios_share"
                                    size="small"
                                    onClick={() => dispatch(openModalImportCustomers({ open: true }))}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={4} className="mb-10 text-right">
                        <SearchInput
                            placeholder={messages["search"]}
                            searchText={paginationCustomers.searchText}
                            onSearchText={paginationCustomers.onSearchChange}
                        />
                    </Grid>
                </Grid>
                <Preferences
                    preferencesKey={KEY_PREFERENCE}
                    onInitialPreferences={(data) => {
                        setPreferences(data)
                    }}
                >
                    <TableView
                        keyColumn="id"
                        loading={loadingCustomers}
                        topheight={260}
                        data={customers || []}
                        total={totalCustomers}
                        exportOrientation="landscape"
                        onSelectedItems={(items) => setSelectedItems(items)}
                        {...paginationCustomers}
                        onChangeColumns={(columns) => {
                            let currentColumns = paginationCustomers?.columns || []
                            let finalsColumns = completeColumns(currentColumns, columns)
                            savePreferences("columns", finalsColumns)
                            paginationCustomers.setColumns(finalsColumns)
                        }}
                        hideAllInPagination
                    />
                </Preferences>

            </Container>
            {openModalCustomer && <CreateEditCustomer refresh={refreshTable} />}
            {openModalImport && <ImportCustomers />}
            {openModalTransactions && <TransactionsSummary />}
            {openModalDelete && <DeleteCustomer refresh={refreshTable} />}
            {
                dataToMap?.show &&
                <MapModal
                    hiddenButton
                    {...dataToMap}
                    hiddenTraffic={true}
                    hiddenWeather={true}
                    hiddenSearchMap={true}
                    hideModal={() => {
                        setDataToMap({ show: false });
                    }}
                    zoom={18}
                />
            }
        </Fragment>
    );
};

export default CustomerTab;
